import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useApplications } from "@/hooks/useApplications";
import { useAppInventory } from "@/hooks/useAppInventory";
import { ApplicationGrid } from "@/components/applications/ApplicationGrid";
import { AppInventoryList } from "@/components/applications/AppInventoryList";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import ErrorDisplay from "@/components/ErrorDisplay";
import { AppWindow, Search, Plus, Upload, ChevronDown, Package } from "lucide-react";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import ViewToggle from "@/components/ViewToggle";
import { ApplicationList } from "@/components/applications/ApplicationList";
export default function ApplicationsPage() {
  const {
    applications,
    loading,
    error,
    updateApplication
  } = useApplications();
  
  const { 
    inventory, 
    isLoading: inventoryLoading, 
    error: inventoryError 
  } = useAppInventory();
  
  const [activeTab, setActiveTab] = useState<"catalog" | "inventory">("catalog");
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedPlatform, setSelectedPlatform] = useState<string>("all");
  const [view, setView] = useState<"grid" | "list">(() => {
    const savedView = localStorage.getItem("applicationsViewPreference");
    return (savedView === "list" || savedView === "grid") ? savedView : "grid";
  });

  useEffect(() => {
    localStorage.setItem("applicationsViewPreference", view);
  }, [view]);

  const filteredApps = applications.filter(app => {
    const matchesSearch = app.name.toLowerCase().includes(searchTerm.toLowerCase()) || app.bundleId?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesPlatform = selectedPlatform === "all" || app.platform === selectedPlatform;
    return matchesSearch && matchesPlatform;
  });
  if (loading) {
    return <div className="px-6 py-6">
        <div className="h-8 w-48 bg-gray-200 rounded animate-pulse mb-8"></div>
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {[1, 2, 3, 4, 5, 6].map(i => <div key={i} className="border rounded-lg p-6 space-y-4">
              <div className="flex items-center gap-3">
                <div className="h-12 w-12 bg-gray-200 rounded-lg animate-pulse"></div>
                <div className="space-y-2">
                  <div className="h-5 w-32 bg-gray-200 rounded animate-pulse"></div>
                  <div className="h-4 w-24 bg-gray-200 rounded animate-pulse"></div>
                </div>
              </div>
              <div className="space-y-2">
                <div className="h-4 w-full bg-gray-200 rounded animate-pulse"></div>
                <div className="h-4 w-3/4 bg-gray-200 rounded animate-pulse"></div>
              </div>
            </div>)}
        </div>
      </div>;
  }
  if (error) {
    return <div className="px-6 py-6">
        <ErrorDisplay error={error} />
      </div>;
  }
  return (
    <div className="px-6 py-6">
      <div className="flex flex-col md:flex-row items-start md:items-center justify-between mb-8 gap-4">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">App Management</h1>
          <p className="text-muted-foreground mt-1">
            Manage applications in your catalog and view inventory across all devices
          </p>
        </div>
        {activeTab === "catalog" && (
          <div className="flex gap-2">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button>
                  <Plus className="mr-2 h-4 w-4" />
                  Add New
                  <ChevronDown className="ml-2 h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem asChild>
                  <Link to="/applications/add" className="flex items-center w-full">
                    <AppWindow className="mr-2 h-4 w-4" />
                    Add from Catalog
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuItem className="flex items-center">
                  <Upload className="mr-2 h-4 w-4" />
                  Upload Custom App
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        )}
      </div>

      <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as "catalog" | "inventory")}>
        <TabsList className="mb-6">
          <TabsTrigger value="catalog">
            <AppWindow className="mr-2 h-4 w-4" />
            Catalog
          </TabsTrigger>
          <TabsTrigger value="inventory">
            <Package className="mr-2 h-4 w-4" />
            Inventory
          </TabsTrigger>
        </TabsList>

        <TabsContent value="catalog">
          <div className="bg-white rounded-lg border shadow-sm p-6">
            <div className="flex flex-col md:flex-row justify-between gap-4 mb-6">
              <div className="relative w-full md:w-96">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
                <Input 
                  className="pl-9" 
                  placeholder="Search applications" 
                  value={searchTerm} 
                  onChange={e => setSearchTerm(e.target.value)} 
                />
              </div>
              <div className="flex items-center gap-4">
                <Tabs defaultValue="all" value={selectedPlatform} onValueChange={setSelectedPlatform} className="w-full md:w-auto">
                  <TabsList className="grid grid-cols-4 w-full md:w-auto">
                    <TabsTrigger value="all">All</TabsTrigger>
                    <TabsTrigger value="ios">iOS</TabsTrigger>
                    <TabsTrigger value="macos">macOS</TabsTrigger>
                    <TabsTrigger value="tvos">tvOS</TabsTrigger>
                  </TabsList>
                </Tabs>
                <ViewToggle view={view} onViewChange={setView} />
              </div>
            </div>

            {filteredApps.length > 0 ? (
              view === "grid" ? (
                <ApplicationGrid applications={filteredApps} onUpdateApplication={updateApplication} />
              ) : (
                <ApplicationList applications={filteredApps} onUpdateApplication={updateApplication} />
              )
            ) : (
              <div className="text-center py-16">
                <h3 className="text-xl font-medium text-gray-500">No applications found</h3>
                {searchTerm ? (
                  <p className="mt-2 text-gray-400">
                    Try adjusting your search or filters
                  </p>
                ) : (
                  <div className="mt-4">
                    <Button asChild>
                      <Link to="/applications/add">Add Applications</Link>
                    </Button>
                  </div>
                )}
              </div>
            )}
          </div>
        </TabsContent>

        <TabsContent value="inventory">
          <div className="bg-white rounded-lg border shadow-sm p-6">
            {inventoryLoading ? (
              <div className="text-center py-12">
                <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-current border-r-transparent" />
                <p className="mt-4 text-muted-foreground">Loading inventory...</p>
              </div>
            ) : inventoryError ? (
              <div className="text-center py-12 text-destructive">
                <p>Error loading inventory: {inventoryError.message}</p>
              </div>
            ) : (
              <AppInventoryList inventory={inventory} />
            )}
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}